/**
 * The page context's sheet and selection, as the composer shows and sends them.
 *
 * Two hooks, one rule: the chip shows exactly what the next send carries.
 * Both read through `readValidDetail`, the same read + validation
 * `toContextItems` runs at send time, so a multi-range selection, a stray `$`
 * or an over-long sheet name can never be shown one way and sent another.
 */
import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from 'react';

import type { ChatContext } from '../../../contexts/ChatContextProvider';
import { readValidDetail } from '../../../hooks/data/agentChatReducer';

type View = { sheet?: string; selection?: string };

// U+0000 cannot occur in a sheet name or an A1 range, so the key is lossless.
const SEPARATOR = '\u0000';
const keyOf = (view: View) => `${view.sheet ?? ''}${SEPARATOR}${view.selection ?? ''}`;
const NO_SUBSCRIPTION = () => () => {};

/**
 * The live, validated sheet/selection of `context`, re-read whenever its
 * `onDetailChange` fires. A context with no `detail` (every manually picked
 * one) reads as empty. Re-renders only when the VALUE changes: the snapshot is
 * a string key, so the many identical events of a drag cost nothing.
 */
export function useLiveContextDetail(context: ChatContext): View {
  const subscribe = context.onDetailChange ?? NO_SUBSCRIPTION;
  const key = useSyncExternalStore(subscribe, () => keyOf(readValidDetail(context.detail)));
  return useMemo(() => {
    const [sheet, selection] = key.split(SEPARATOR);
    return { sheet: sheet || undefined, selection: selection || undefined };
  }, [key]);
}

/**
 * Lets the researcher stop sending the selected cells while keeping the
 * spreadsheet (and its sheet) in context.
 *
 * Returns the page context with its `detail` wrapped: while the view on screen
 * is still the one that was dismissed, `selection` is left out, so the chip
 * and `toContextItems` both drop it. Any DIFFERENT view (a new selection, or
 * another tab) ends the dismissal, so that selection is shown and sent again.
 * Re-selecting the very same range while it is dismissed is not a change of
 * view, so it stays dismissed.
 *
 * A different page context or a new chat (`resetKey`) also ends it, and so
 * does reopening the chat panel: collapsing it unmounts the composer and this
 * state with it. Accepted: the chip shows the range again, so what the chip
 * shows and what the send carries still agree.
 *
 * Why match on the view rather than a plain flag cleared on any event: the
 * page's `onDetailChange` also fires with no user action (the grid fires every
 * listener once when it mounts or remounts), and that must not bring back a
 * range the researcher just removed.
 */
export function useSelectionDismissal(
  context: ChatContext | null,
  /** The composer's `autoContextResetKey`: a new chat starts undismissed, like
   *  every other composer choice. */
  resetKey?: string,
): {
  readonly context: ChatContext | null;
  readonly dismissSelection: () => void;
} {
  const [dismissed, setDismissed] = useState<string | null>(null);

  // A different page context, or a new chat, starts undismissed.
  const contextKey = context ? `${context.type}:${context.id}` : null;
  useEffect(() => setDismissed(null), [contextKey, resetKey]);

  // Only listens while something is dismissed: that is the only time a view
  // change has anything to undo.
  const onDetailChange = context?.onDetailChange;
  const detail = context?.detail;
  useEffect(() => {
    if (dismissed === null || !onDetailChange) return undefined;
    return onDetailChange(() => {
      if (keyOf(readValidDetail(detail)) !== dismissed) setDismissed(null);
    });
  }, [dismissed, onDetailChange, detail]);

  const dismissSelection = useCallback(() => {
    setDismissed(keyOf(readValidDetail(detail)));
  }, [detail]);

  const wrapped = useMemo<ChatContext | null>(() => {
    if (!context?.detail || dismissed === null) return context;
    const read = context.detail;
    return {
      ...context,
      detail: () => {
        const view = read();
        if (keyOf(readValidDetail(() => view)) !== dismissed) return view;
        return { sheet: view.sheet };
      },
    };
  }, [context, dismissed]);

  return { context: wrapped, dismissSelection };
}
